import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CircularProgress from "@mui/material/CircularProgress";
import FinanceService from "Lib/API/Finance";
import { getAccessTokenSilently } from "utils/functions";
import './tranzilaCard.scss';

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 3 * 60 * 1000; // 3 minutes

const TranzilaCard = ({
  terminalName,
  onSaveCard,
  onCharged,
  loading,
  setLoading,
  saveCard,
  amount,
  currency,
  email,
  contactName,
  paymentMethodId,
  orgId,
  onHandshake,
}) => {
  const iframeRef = useRef(null);
  const initialLoadedRef = useRef(false);
  const pollTimerRef = useRef(null);
  const pollDeadlineRef = useRef(null);

  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [thtk, setThtk] = useState('');
  const [handshakeError, setHandshakeError] = useState('');
  const [pollError, setPollError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [handshakeSumValue, setHandshakeSumValue] = useState(null); // converted amount from backend

  const currencyCode = useMemo(() => {
    if (currency === 'ILS' || currency === 'NIS') return '1';
    if (currency === 'USD') return '2';
    if (currency === 'EUR') return '978';
    if (currency === 'GBP') return '826';
    return '1';
  }, [currency]);

  const tranmode = saveCard ? 'VK' : 'A';
  const sumValue = saveCard ? 1 : Number(amount || 1);

  // Attempt handshake for authenticated users; fall back to random session ID for unauthenticated
  useEffect(() => {
    if (!terminalName) return;
    let cancelled = false;
    initialLoadedRef.current = false;
    setThtk('');
    setHandshakeError('');
    setPollError('');
    setIframeLoaded(false);
    setHandshakeSumValue(null);

    (async () => {
      try {
        const token = await getAccessTokenSilently();
        if (!token) throw new Error('no token');
        setIsAuthenticated(true);
        console.log('[TranzilaCard] requesting handshake, amount:', sumValue.toFixed(2));
        const result = await FinanceService.tranzilaHandshake(token, {
          amount: sumValue.toFixed(2),
        });
        console.log('[TranzilaCard] handshake result:', result);
        if (!cancelled) setThtk(result?.thtk || '');
      } catch {
        // Unauthenticated — use public handshake if paymentMethodId+orgId provided
        if (paymentMethodId && orgId) {
          try {
            console.log('[TranzilaCard] public handshake, amount:', sumValue.toFixed(2));
            const result = await FinanceService.tranzilaHandshakePublic({
              paymentMethodId,
              orgId,
              amount: sumValue.toFixed(2),
              currency,
            });
            console.log('[TranzilaCard] public handshake result:', result);
            if (!cancelled) {
              setThtk(result?.thtk || '');
              if (result?.convertedAmount) {
                setHandshakeSumValue(result.convertedAmount);
                if (onHandshake) onHandshake({ convertedAmount: result.convertedAmount, currency: result.currency });
              }
            }
            return;
          } catch (pubErr) {
            console.error('[TranzilaCard] public handshake failed:', pubErr?.message);
          }
        }
        // Fall back to direct session (no thtk, no callback URLs)
        const sessionId = `direct_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        console.log('[TranzilaCard] falling back to direct session:', sessionId);
        if (!cancelled) setThtk(sessionId);
      }
    })();

    return () => { cancelled = true; };
  }, [terminalName, sumValue, currency]);

  const apiBase = process.env.REACT_APP_TRANZILA_CALLBACK_URL || process.env.REACT_APP_BASE_URL || '';

  const hasRealThtk = thtk?.startsWith('w');

  const iframeSrc = useMemo(() => {
    if (!terminalName || !thtk) return '';
    const params = new URLSearchParams({
      ...(hasRealThtk && { new_process: '1' }), // only with a real Tranzila thtk
      sum: (handshakeSumValue || sumValue).toFixed(2),
      currency: currencyCode,
      cred_type: '1',
      tranmode,
      nologo: '1',
      lang: 'us',
      trBgColor: 'FFFFFF',
      trTextColor: '333333',
      trButtonColor: '2555EF',
      buttonLabel: saveCard ? 'Save Card' : 'Pay Now',
    });

    // Always set success/fail/notify URLs — works for both auth (thtk from handshake) and unauth (local session ID)
    if (hasRealThtk) params.set('thtk', thtk);
    params.set('success_url_address', `${apiBase}/finance/v1/payment/tranzila/result?thtk=${thtk}`);
    params.set('fail_url_address', `${apiBase}/finance/v1/payment/tranzila/result?thtk=${thtk}&failed=1`);
    params.set('notify_url_address', `${apiBase}/finance/v1/payment/tranzila/notify?thtk=${thtk}`);

    if (email) params.set('contact', email);
    if (contactName) params.set('contact_name', contactName);
    return `https://direct.tranzila.com/${terminalName}/iframenew.php?${params.toString()}`;
  }, [terminalName, thtk, hasRealThtk, sumValue, handshakeSumValue, currencyCode, tranmode, saveCard, email, contactName, apiBase]);

  useEffect(() => {
    if (iframeSrc) {
      console.log('[TranzilaCard] iframe src:', iframeSrc);
      // Reset load tracker and clear any previous errors whenever the iframe URL changes
      initialLoadedRef.current = false;
      setIframeLoaded(false);
      setPollError('');
      clearTimeout(pollTimerRef.current);
    }
  }, [iframeSrc]);

  // Poll backend for result after iframe submission (second onLoad)
  const startPolling = useCallback(async () => {
    if (!thtk) return;
    console.log('[TranzilaCard] starting poll for thtk:', thtk);
    pollDeadlineRef.current = Date.now() + POLL_TIMEOUT_MS;

    const poll = async () => {
      if (Date.now() > pollDeadlineRef.current) {
        console.error('[TranzilaCard] poll timed out');
        setPollError('Payment result timed out. Please check if the payment was processed.');
        return;
      }
      try {
        let response;
        if (isAuthenticated) {
          const token = await getAccessTokenSilently();
          response = await FinanceService.tranzilaIframePoll(token, thtk);
        } else {
          response = await FinanceService.tranzilaIframePollPublic(thtk);
        }
        console.log('[TranzilaCard] poll response:', response);
        if (response?.ready) {
          const result = response.result;
          console.log('[TranzilaCard] transaction result:', result);
          if (result?.failed === '1' || (result?.Response && result?.Response !== '000')) {
            setPollError(result?.error_msg || 'Payment failed');
            return;
          }
          const token = result?.TranzilaTK;
          if (onSaveCard && token) onSaveCard(token, result);
          if (onCharged) onCharged({ ...result, token });
        } else {
          pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
        }
      } catch (error) {
        console.error('[TranzilaCard] poll error:', error);
        setPollError('Could not retrieve payment result');
      }
    };
    poll();
  }, [thtk, isAuthenticated, onSaveCard, onCharged]);

  useEffect(() => {
    return () => clearTimeout(pollTimerRef.current);
  }, []);

  const handleIframeLoad = useCallback(() => {
    if (!initialLoadedRef.current) {
      console.log('[TranzilaCard] iframe loaded (initial)');
      initialLoadedRef.current = true;
      setIframeLoaded(true);
    } else {
      console.log('[TranzilaCard] iframe loaded (post-submission) — starting poll');
      startPolling();
    }
  }, [startPolling]);

  if (!terminalName) {
    console.error('[TranzilaCard] terminal not configured');
    return <div className="tranzila-card__error">Tranzila terminal not configured</div>;
  }

  if (handshakeError) {
    return <div className="tranzila-card__error">Tranzila handshake failed: {handshakeError}</div>;
  }

  if (pollError) {
    return <div className="tranzila-card__error">{pollError}</div>;
  }

  return (
    <div className="tranzila-card">
      {(!iframeLoaded || !iframeSrc) && (
        <div className="tranzila-card__loading">
          <CircularProgress size={24} />
          <span>Loading payment form...</span>
        </div>
      )}
      {saveCard && (
        <p className="tranzila-card__note">
          A temporary {currencyCode === '1' ? '₪' : '$'}1 verification hold will be placed and automatically released.
        </p>
      )}
      <iframe
        ref={iframeRef}
        src={iframeSrc}
        title="Tranzila Payment"
        className="tranzila-card__iframe"
        onLoad={handleIframeLoad}
        onError={(e) => console.error('[TranzilaCard] iframe failed to load:', e)}
        allow="payment"
        sandbox="allow-scripts allow-same-origin allow-forms allow-top-navigation"
      />
    </div>
  );
};

export default TranzilaCard;
